/**
 * Free server-side A/V relay for Live Coding when WebRTC/TURN cannot connect.
 * Sends JPEG video frames + audio/webm chunks over Socket.IO (your own backend).
 * No third-party TURN fees — uses your existing server bandwidth.
 */

const VIDEO_FPS = 8;
const VIDEO_WIDTH = 640;
const VIDEO_JPEG_QUALITY = 0.55;
const AUDIO_TIMESLICE_MS = 400;

function pickAudioMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || null;
}

/**
 * @param {object} opts
 * @param {() => import('socket.io-client').Socket | null} opts.getSocket
 * @param {() => string | null} opts.getRoomId
 * @param {() => MediaStream | null} opts.getLocalStream — camera+mic
 * @param {() => MediaStream | null} [opts.getScreenStream]
 * @param {() => HTMLVideoElement | null} opts.getRemoteVideoEl
 * @param {() => HTMLVideoElement | null} [opts.getStageVideoEl]
 * @param {() => boolean} [opts.isRemoteScreenSharing]
 */
export function createLiveMediaRelay(opts) {
  let active = false;
  let videoTimer = null;
  let audioRecorder = null;
  let canvas = null;
  let ctx = null;
  let sourceVideo = null;
  let remoteAudioEl = null;
  let remoteImgUrl = null;
  let audioMime = null;

  const ensureCanvas = () => {
    if (!canvas) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { alpha: false });
    }
    return { canvas, ctx };
  };

  const ensureSourceVideo = (stream) => {
    if (!sourceVideo) {
      sourceVideo = document.createElement('video');
      sourceVideo.muted = true;
      sourceVideo.playsInline = true;
      sourceVideo.autoplay = true;
    }
    if (sourceVideo.srcObject !== stream) {
      sourceVideo.srcObject = stream;
      sourceVideo.play?.().catch(() => {});
    }
    return sourceVideo;
  };

  const emit = (type, data) => {
    const socket = opts.getSocket?.();
    const roomId = opts.getRoomId?.();
    if (!socket || !roomId || !data) return;
    socket.emit('media_relay', {
      roomId,
      type,
      data,
      ts: Date.now(),
    });
  };

  const paintAndSend = () => {
    if (!active) return;
    const screen = opts.getScreenStream?.();
    const local = screen || opts.getLocalStream?.();
    if (!local) return;

    const videoTrack = local.getVideoTracks?.()?.[0];
    if (!videoTrack || videoTrack.readyState !== 'live' || !videoTrack.enabled) {
      return;
    }

    const v = ensureSourceVideo(local);
    if (!v.videoWidth) return;

    const { canvas: c, ctx: cctx } = ensureCanvas();
    const isScreen = !!screen;
    const targetW = isScreen ? 960 : VIDEO_WIDTH;
    const scale = targetW / v.videoWidth;
    c.width = targetW;
    c.height = Math.max(1, Math.round(v.videoHeight * scale));
    cctx.drawImage(v, 0, 0, c.width, c.height);

    c.toBlob(
      (blob) => {
        if (!blob || !active) return;
        blob.arrayBuffer().then((buf) => {
          emit(isScreen ? 'screen-jpeg' : 'video-jpeg', buf);
        });
      },
      'image/jpeg',
      isScreen ? 0.6 : VIDEO_JPEG_QUALITY,
    );
  };

  const startAudio = () => {
    stopAudio();
    const local = opts.getLocalStream?.();
    const audioTracks = local?.getAudioTracks?.() || [];
    if (!audioTracks.length) return;

    audioMime = pickAudioMime();
    if (!audioMime) return;

    try {
      const audioOnly = new MediaStream(audioTracks);
      audioRecorder = new MediaRecorder(audioOnly, {
        mimeType: audioMime,
        audioBitsPerSecond: 24000,
      });
      audioRecorder.ondataavailable = (e) => {
        if (!active || !e.data || e.data.size < 1) return;
        e.data.arrayBuffer().then((buf) => emit('audio-chunk', buf));
      };
      audioRecorder.start(AUDIO_TIMESLICE_MS);
    } catch (err) {
      console.warn('[relay] audio recorder failed:', err);
      audioRecorder = null;
    }
  };

  const stopAudio = () => {
    if (audioRecorder) {
      try {
        if (audioRecorder.state !== 'inactive') audioRecorder.stop();
      } catch (_) {
        /* ignore */
      }
      audioRecorder = null;
    }
  };

  const showRemoteFrame = (arrayBuffer, ontoStage) => {
    const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    if (remoteImgUrl) URL.revokeObjectURL(remoteImgUrl);
    remoteImgUrl = url;

    const applyTo = (el) => {
      if (!el) return;
      // Use video element as display surface via poster-like img overlay isn't ideal;
      // draw into a canvas sibling if needed — for simplicity set as srcObject null and use background via Image
      // Prefer painting to a canvas we attach as the element's replacement via captureStream — keep it simple:
      if (!el._relayCanvas) {
        const wrap = el.parentElement;
        const c = document.createElement('canvas');
        c.style.cssText =
          'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#010409;';
        c.dataset.relay = '1';
        if (wrap) {
          wrap.style.position = wrap.style.position || 'relative';
          wrap.appendChild(c);
        }
        el._relayCanvas = c;
        el.style.visibility = 'hidden';
      }
      const c = el._relayCanvas;
      const img = new Image();
      img.onload = () => {
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const cctx = c.getContext('2d');
        cctx.drawImage(img, 0, 0);
      };
      img.src = url;
    };

    applyTo(opts.getRemoteVideoEl?.());
    if (ontoStage) applyTo(opts.getStageVideoEl?.());
  };

  const playRemoteAudio = (arrayBuffer) => {
    const mime = audioMime || 'audio/webm';
    const blob = new Blob([arrayBuffer], { type: mime });
    const url = URL.createObjectURL(blob);
    if (!remoteAudioEl) {
      remoteAudioEl = document.createElement('audio');
      remoteAudioEl.autoplay = true;
      remoteAudioEl.playsInline = true;
      document.body.appendChild(remoteAudioEl);
    }
    const sinkId = opts.getAudioOutputId?.();
    const playChunk = async () => {
      try {
        if (sinkId && typeof remoteAudioEl.setSinkId === 'function') {
          await remoteAudioEl.setSinkId(sinkId).catch(() => {});
        }
        remoteAudioEl.src = url;
        remoteAudioEl.muted = false;
        await remoteAudioEl.play?.();
      } catch (_) {
        /* autoplay / decode */
      } finally {
        // Revoke after a beat so playback can start
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    };
    playChunk();
  };

  const clearRemoteOverlays = () => {
    [opts.getRemoteVideoEl?.(), opts.getStageVideoEl?.()].forEach((el) => {
      if (el?._relayCanvas) {
        el._relayCanvas.remove();
        el._relayCanvas = null;
        el.style.visibility = '';
      }
    });
    if (remoteImgUrl) {
      URL.revokeObjectURL(remoteImgUrl);
      remoteImgUrl = null;
    }
  };

  return {
    get active() {
      return active;
    },

    start() {
      if (active) return;
      active = true;
      const socket = opts.getSocket?.();
      const roomId = opts.getRoomId?.();
      socket?.emit('media_relay_mode', { roomId, enabled: true });

      videoTimer = setInterval(paintAndSend, Math.round(1000 / VIDEO_FPS));
      startAudio();
      console.log('[relay] server media relay started (free fallback)');
    },

    // The MediaRecorder is bound to a fixed snapshot of the audio track(s)
    // taken at start()/restartAudio() time — unlike paintAndSend(), which
    // re-reads getLocalStream() every frame. If the caller swaps the mic
    // (stops the old track, adds a new one), that recorder's only source
    // track is now ended and it will never emit audio again unless we
    // rebuild it here.
    restartAudio() {
      if (!active) return;
      startAudio();
    },

    stop() {
      if (!active) return;
      active = false;
      if (videoTimer) {
        clearInterval(videoTimer);
        videoTimer = null;
      }
      stopAudio();
      clearRemoteOverlays();
      const socket = opts.getSocket?.();
      const roomId = opts.getRoomId?.();
      socket?.emit('media_relay_mode', { roomId, enabled: false });
      console.log('[relay] server media relay stopped');
    },

    /** Peer told us they enabled/disabled relay */
    onRemoteMode(enabled) {
      if (!enabled) clearRemoteOverlays();
    },

    onPacket({ type, data }) {
      if (!data) return;
      const buf = data instanceof ArrayBuffer ? data : data?.buffer || data;
      if (!buf) return;

      if (type === 'video-jpeg') {
        showRemoteFrame(buf, false);
      } else if (type === 'screen-jpeg') {
        showRemoteFrame(buf, true);
        showRemoteFrame(buf, false);
      } else if (type === 'audio-chunk') {
        playRemoteAudio(buf);
      }
    },
  };
}
